import { useState } from 'react';

import { InfoIcon } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from 'components/Dialog';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

type ProdTMSWarningBannerProps = {
  tmsName?: string;
};

export function ProdTMSWarningBanner({ tmsName }: ProdTMSWarningBannerProps) {
  return (
    <Flex
      direction='row'
      gap='sm'
      align='start'
      className='mt-3 rounded border border-warning-200 bg-warning-100 px-3 py-2'
    >
      <InfoIcon className='h-4 w-4 shrink-0 mt-0.5 text-info-600' />
      <Typography variant='body-xs' className='text-neutral-600 italic'>
        Non-production Drumkit is connected to a production{' '}
        {tmsName ? `${tmsName} ` : ''}TMS. You can only build loads for test
        customers unless intentionally overridden upon submission.
      </Typography>
    </Flex>
  );
}

export type ProdTMSGuardrailChoice =
  'cancel' | 'override_once' | 'mark_test_customer';

type ProdTMSGuardrailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName?: string;
  tmsName?: string;
  onConfirm: (choice: ProdTMSGuardrailChoice) => void | Promise<void>;
  isLoading?: boolean;
};

export function ProdTMSGuardrailDialog({
  open,
  onOpenChange,
  customerName,
  tmsName,
  onConfirm,
  isLoading,
}: ProdTMSGuardrailDialogProps) {
  const [pendingChoice, setPendingChoice] =
    useState<ProdTMSGuardrailChoice | null>(null);

  const handleChoice = async (choice: ProdTMSGuardrailChoice) => {
    if (choice === 'cancel') {
      onOpenChange(false);
      return;
    }
    setPendingChoice(choice);
    try {
      await onConfirm(choice);
    } finally {
      setPendingChoice(null);
    }
  };

  const loading = isLoading || pendingChoice !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Flex direction='col' gap='sm' className='p-4'>
          <DialogTitle>Production TMS Warning</DialogTitle>
          <DialogDescription>
            <Typography variant='body-sm'>
              You are on a non-production Drumkit environment but this{' '}
              {tmsName ? `${tmsName} ` : ''}connection points at a production
              TMS.
            </Typography>
            <Typography variant='body-sm' className='mt-2'>
              Building a load for{' '}
              <span className='font-medium'>
                {customerName?.trim() || 'this customer'}
              </span>{' '}
              may create real data in the customer&apos;s TMS.
            </Typography>
            <Typography variant='body-sm' className='mt-2' weight='medium'>
              How do you want to continue?
            </Typography>
          </DialogDescription>
          <Flex direction='col' gap='sm' className='w-full mt-4'>
            <Button
              variant='default'
              size='sm'
              onClick={() => handleChoice('override_once')}
              disabled={loading}
              buttonNamePosthog={ButtonNamePosthog.BuildLoad}
              className='text-sm w-full'
            >
              {pendingChoice === 'override_once'
                ? 'Continuing...'
                : 'Build this load only (one-time override)'}
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => handleChoice('mark_test_customer')}
              disabled={loading}
              buttonNamePosthog={null}
              className='border-neutral-900 text-sm w-full'
            >
              {pendingChoice === 'mark_test_customer'
                ? 'Saving...'
                : 'Mark customer as test & build load'}
            </Button>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => handleChoice('cancel')}
              disabled={loading}
              buttonNamePosthog={null}
              className='text-sm w-full'
            >
              Cancel
            </Button>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog>
  );
}
